// ---------------------------------------------------------------------------
// LA CAISSE CÔTÉ PAGE — le prix au moment d'OUVRIR un groupe, jamais ailleurs
// ---------------------------------------------------------------------------
// Décisions de Toji (22/10, vault : Proposition-Points-Activite-2026-10-22) :
//   · le prix n'apparaît qu'au moment d'OUVRIR un groupe, jamais pour rejoindre ;
//   · « Rejoindre » est le bouton principal quand un groupe existe sur l'œuvre ;
//   · une ligne : prix barré + économie + solde après ;
//   · aucune confirmation sous 50 points (annulable 10 min) ;
//   · la file montre son rang et propose les groupes en cours, jamais un refus sec.
// Le même client sert le site et l'activité Discord.
//
// ⚠️ LE PRIX AFFICHÉ EST UNE PROMESSE, LE PRIX DÉBITÉ EST CELUI DU SERVEUR
// (`apps/player/caisse.js` recalcule au clic). Ce module n'invente aucun chiffre.
// ---------------------------------------------------------------------------

const MOTS = {
  creuses: 'heures creuses', suivant: 'épisode suivant', cache: 'déjà en cache',
  promo: 'promo', fidelite: 'fidélité', serie: 'série complète',
};

const jourCourt = (iso) => {
  const [a, m, j] = String(iso ?? '').split('-').map(Number);
  return a ? new Date(a, m - 1, j).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : '';
};

/** Les raisons d'une remise, en clair : « épisode suivant −20 %, promo −20 % jusqu'au 31 octobre ». */
export function raisonsDe(remises = []) {
  return remises.map((x) => `${MOTS[x.motif] ?? x.motif} −${x.pourcent} %${x.fin ? ` jusqu’au ${jourCourt(x.fin)}` : ''}`).join(', ');
}

/** Les morceaux de LA ligne de prix (pure, éprouvée par essais/caisse-page.mjs). */
export function lignePrix(d = {}) {
  const prix = Number(d.prix) || 0;
  const ref = Number(d.reference) || 0;
  return {
    barre: ref > prix ? ref : null,
    prix,
    economie: ref > prix ? ref - prix : 0,
    raisons: raisonsDe(d.remises ?? []),
    apres: Number.isFinite(d.soldeApres) ? d.soldeApres : null,
    manque: Number(d.manque) > 0 ? Number(d.manque) : 0,
  };
}

/** La ligne en texte (titres, lecteurs d'écran) : « 36 → 24 pts · −12 (épisode suivant −20 %) · solde après : 116 ». */
export function textePrix(d) {
  const l = lignePrix(d);
  return [
    l.barre ? `${l.barre} → ${l.prix} pts` : `${l.prix} pts`,
    l.economie ? `−${l.economie}${l.raisons ? ` (${l.raisons})` : ''}` : null,
    l.apres !== null ? `solde après : ${l.apres}` : null,
  ].filter(Boolean).join(' · ');
}

const el = (balise, classe, texte) => {
  const n = document.createElement(balise);
  if (classe) n.className = classe;
  if (texte !== undefined && texte !== null) n.textContent = texte;
  return n;
};

/** La ligne de prix en DOM : prix barré, prix, économie, solde après — une ligne. */
function noeudPrix(d) {
  const l = lignePrix(d);
  const p = el('p', 'caisse-ligne');
  if (l.barre) p.append(el('s', 'caisse-barre', `${l.barre}`), ' ');
  p.append(el('b', 'caisse-prix', `${l.prix} pts`));
  if (l.economie) p.append(el('span', 'caisse-eco', ` · −${l.economie}`));
  if (l.apres !== null) p.append(el('span', 'caisse-apres', ` · solde après : ${l.apres}`));
  if (!l.raisons) return p;
  // Les raisons sous la ligne, en petit : la ligne elle-même reste une ligne.
  const f = document.createDocumentFragment();
  f.append(p, el('p', 'caisse-raisons', l.raisons));
  return f;
}

export function monterCaisse({ api, direMot = () => {}, tracer = () => {}, rejoindre = (alias) => { location.href = `/lecteur/${encodeURIComponent(alias)}`; } }) {
  let actif = false;
  let solde = null;
  let confirmerDes = 50;
  const ecouteurs = new Set();

  function majSolde(n) {
    if (Number.isFinite(n)) solde = n;
    const puce = document.getElementById('pp-solde');
    if (puce) {
      puce.hidden = !actif || solde === null;
      puce.textContent = solde === null ? '' : `${solde} pts`;
    }
    for (const f of ecouteurs) { try { f(solde); } catch { /* un écouteur ne casse pas les autres */ } }
  }

  /** Le solde et le journal ; dit aussi si la caisse est en marche. */
  async function chargerMoi() {
    try {
      const m = await api('/api/points/moi');
      actif = !!m.actif;
      confirmerDes = Number(m.confirmerDes) || 50;
      majSolde(m.solde);
      return m;
    } catch (err) {
      tracer('caisse', 'solde indisponible', err?.message);
      return null;
    }
  }

  // --- la feuille : ouvrir, file, manque -------------------------------------
  let feuilleOuverte = null;
  function fermerFeuille(valeur) {
    const f = feuilleOuverte;
    feuilleOuverte = null;
    if (!f) return;
    clearInterval(f.minuteur);
    f.voile.remove();
    if (f.attend) api('/api/points/file', { method: 'DELETE' }).catch(() => {});
    f.resoudre(valeur);
  }

  function boutonsRejoindre(liste = [], { principal = true } = {}) {
    return liste.map((g, i) => {
      const b = el('button', `ss-bouton${principal && i === 0 ? ' primaire' : ''}`, `Rejoindre ${g.titre ?? 'le groupe'} (${g.personnes} ${g.personnes > 1 ? 'personnes' : 'personne'})`);
      b.type = 'button';
      b.onclick = () => { fermerFeuille(false); rejoindre(g.alias); };
      return b;
    });
  }

  /**
   * La feuille de prix. Résout `true` si l'on a payé (la lecture part), `false`
   * sinon (annulé, parti rejoindre un groupe, encore dans la file).
   */
  function feuille(d, { type, id, titre, payer, mots = {} }) {
    if (feuilleOuverte) fermerFeuille(false);
    return new Promise((resoudre) => {
      const voile = el('div', 'caisse-voile');
      const boite = el('div', 'caisse-feuille');
      boite.setAttribute('role', 'dialog');
      boite.setAttribute('aria-modal', 'true');
      voile.append(boite);
      voile.addEventListener('click', (e) => { if (e.target === voile) fermerFeuille(false); });
      const f = { voile, resoudre, minuteur: null, attend: false };
      feuilleOuverte = f;

      const peindre = (dv) => {
        boite.textContent = '';
        const complet = dv.action === 'complet';
        boite.append(el('h2', 'caisse-titre', complet ? 'Tous les groupes sont pris' : (mots.titre ?? 'Ouvrir un groupe')));
        if (titre) boite.append(el('p', 'caisse-quoi', titre));
        const actions = el('div', 'caisse-actions');
        const rejoints = boutonsRejoindre(dv.rejoindre ?? [], { principal: true });
        if (complet) {
          const place = dv.file;
          boite.append(el('p', 'caisse-file', place
            ? `Tu es ${place.rang === 1 ? '1er' : `${place.rang}e`} dans la file · une place dans ~${place.attenteMin} min`
            : 'Une place se libère à la fin d’un épisode en cours.'));
          if (rejoints.length) boite.append(el('p', 'caisse-aide', 'En attendant, rejoindre un groupe est gratuit :'));
          actions.append(...rejoints);
          if (!place) {
            const b = el('button', `ss-bouton${rejoints.length ? '' : ' primaire'}`, 'Prendre ma place dans la file');
            b.type = 'button';
            b.onclick = () => attendre();
            actions.append(b);
          }
        } else {
          boite.append(noeudPrix(dv));
          const l = lignePrix(dv);
          if (l.manque) boite.append(el('p', 'caisse-manque', `Il te manque ${l.manque} points. Rejoindre un groupe est gratuit et en rapporte.`));
          actions.append(...rejoints);
          const ouvrir = el('button', `ss-bouton${rejoints.length ? '' : ' primaire'}`, `${mots.bouton ?? 'Ouvrir'} — ${dv.prix} pts`);
          ouvrir.type = 'button';
          ouvrir.disabled = l.manque > 0;
          ouvrir.onclick = async () => {
            ouvrir.disabled = true;
            const ok = await payer();
            if (ok === true) fermerFeuille(true);
            else if (ok && typeof ok === 'object') peindre(ok);
            else ouvrir.disabled = false;
          };
          actions.append(ouvrir);
        }
        const non = el('button', 'ss-bouton', complet && f.attend ? 'Quitter la file' : 'Annuler');
        non.type = 'button';
        non.onclick = () => fermerFeuille(false);
        actions.append(non);
        boite.append(actions);
      };

      async function attendre() {
        f.attend = true;
        const tour = async () => {
          try {
            const r = await api(`/api/points/file/${type}/${encodeURIComponent(id)}`, { method: 'POST', body: '{}' });
            if (feuilleOuverte !== f) return;
            peindre(r.devis.action === 'complet' ? { ...r.devis, file: r.place } : r.devis);
          } catch (err) { tracer('caisse', 'file indisponible', err?.message); }
        };
        await tour();
        f.minuteur = setInterval(tour, 15_000);
      }

      peindre(d);
      document.body.append(voile);
      boite.querySelector('.ss-bouton.primaire, .ss-bouton')?.focus?.();
    });
  }

  /** Le reçu : « −24 pts · solde 116 · Annuler » pendant 10 min. */
  function recu(r, libelle = 'Groupe ouvert') {
    document.getElementById('caisse-recu')?.remove();
    if (!(r?.prix > 0)) return;
    const z = el('div', 'caisse-recu');
    z.id = 'caisse-recu';
    z.setAttribute('role', 'status');
    z.append(el('span', null, `${libelle} · −${r.prix} pts · solde ${r.solde}`));
    const b = el('button', 'caisse-annuler', 'Annuler');
    b.type = 'button';
    b.onclick = async () => {
      b.disabled = true;
      try {
        const x = await api('/api/points/annuler', { method: 'POST', body: JSON.stringify({ jeton: r.jeton }) });
        majSolde(x.solde);
        direMot(`Achat annulé : ${x.rendu} points rendus`);
      } catch (err) { direMot(err?.corps?.motif === 'trop-tard' ? 'Trop tard pour annuler (10 min).' : 'Annulation impossible pour l’instant.'); }
      z.remove();
    };
    z.append(b);
    document.body.append(z);
    // Le reçu s'efface au bout de 12 s ; l'annulation reste possible 10 min
    // depuis « Mes points » (le journal garde le jeton).
    setTimeout(() => z.remove(), 12_000);
  }

  async function payerGroupe(type, id, titre) {
    try {
      const r = await api(`/api/points/ouvrir/${type}/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ titre }) });
      majSolde(r.solde);
      recu(r);
      return true;
    } catch (err) {
      const c = err?.corps;
      if (c?.devis) return c.devis; // pas assez, ou complet entre-temps : la feuille se repeint
      direMot('Les points ne répondent pas — réessaie dans un instant.');
      return false;
    }
  }

  /**
   * LA PORTE : la lecture peut-elle partir ? Rejoindre, déjà payé, libre : oui,
   * sans rien montrer. Ouvrir sous 50 points, sans groupe à rejoindre : on paie
   * tout de suite (annulable). Sinon : la feuille.
   */
  async function porteDuPrix(type, id, { titre = null, qualite = null } = {}) {
    if (!actif) return true;
    let d;
    try {
      d = await api(`/api/points/devis/${type}/${encodeURIComponent(id)}${qualite ? `?qualite=${encodeURIComponent(qualite)}` : ''}`);
    } catch (err) {
      // Caisse muette : on laisse la porte du serveur trancher (play-now).
      tracer('caisse', 'devis indisponible', err?.message);
      return true;
    }
    if (!d || ['libre', 'deja', 'rejoindre'].includes(d.action)) return true;
    majSolde(d.solde);
    const payer = () => payerGroupe(type, id, titre);
    if (d.action === 'ouvrir' && !d.confirmer && !(d.manque > 0) && !(d.rejoindre ?? []).length) {
      const ok = await payer();
      if (ok === true) return true;
      if (!ok) return false;
      d = ok;
    }
    return feuille(d, { type, id, titre, payer });
  }

  /** La série complète : toujours confirmée (c'est gros), annulable 10 min. */
  async function acheterSerie(serieId, titre) {
    let d;
    try { d = await api(`/api/points/serie/${encodeURIComponent(serieId)}`); } catch { return false; }
    if (d.action !== 'acheter') return d.action === 'deja';
    const payer = async () => {
      try {
        const r = await api(`/api/points/serie/${encodeURIComponent(serieId)}`, { method: 'POST', body: '{}' });
        majSolde(r.solde);
        recu(r, 'Série débloquée');
        return true;
      } catch (err) { return err?.corps?.devis ?? false; }
    };
    return feuille({ ...d, action: 'ouvrir' }, { type: 'series', id: `${serieId}:0:0`, titre: `${titre ?? 'Cette série'} — ${d.episodes} épisodes`, payer, mots: { titre: 'Toute la série', bouton: 'Débloquer' } })
      .then((ok) => { if (ok) direMot('Toute la série est à toi.'); return ok; });
  }

  // --- « En ce moment », en tête de l'accueil ---------------------------------
  /** `seances` : la réponse de `/api/seances`. Cachée sans séance. */
  function peindreEnCeMoment(zone, seances = [], { ouvrirSeance = null } = {}) {
    if (!zone) return;
    zone.textContent = '';
    const liste = seances.filter((s) => s.alias && s.ouvrir);
    zone.hidden = liste.length === 0;
    if (!liste.length) return;
    zone.append(el('p', 'caisse-em-titre', 'En ce moment'));
    const rang = el('div', 'caisse-em-liste');
    for (const s of liste) {
      const n = Math.max(s.regardent || 0, s.participants || 0);
      const carte = el('div', 'caisse-em');
      if (s.vignette) {
        const img = el('img', 'caisse-em-img');
        img.src = s.vignette;
        img.alt = '';
        img.loading = 'lazy';
        carte.append(img);
      }
      const marque = s.absolu > 0 ? `E${s.absolu}` : (s.ouvrir?.saison ? `S${s.ouvrir.saison}E${s.ouvrir.episode}` : '');
      const txt = el('div', 'caisse-em-txt');
      const nom = s.oeuvreTitre ?? s.titre ?? 'une œuvre';
      // Le titre de séance porte souvent déjà « S1E2 » : ne pas le répéter.
      txt.append(el('b', null, `${n} ${n > 1 ? 'personnes regardent' : 'personne regarde'}`), el('span', null, `${nom}${marque && !nom.includes(marque) ? ` · ${marque}` : ''}`));
      const b = el('button', 'ss-bouton primaire', s.jeRegarde ? 'Revenir' : 'Rejoindre');
      b.type = 'button';
      b.onclick = () => (ouvrirSeance ? ouvrirSeance(s) : rejoindre(s.alias));
      carte.append(txt, b);
      rang.append(carte);
    }
    zone.append(rang);
  }

  // --- la fiche : « Rejoindre » d'abord, puis la série complète -------------
  async function peindreActionsFiche(zone, oeuvre, seances = [], { estUnFilm = false } = {}) {
    if (!zone || !oeuvre?.id) return;
    zone.textContent = '';
    const ici = seances.filter((s) => s.alias && (s.ouvrir?.serie ?? s.ouvrir?.film) === oeuvre.id && !s.jySuis);
    for (const s of ici.slice(0, 2)) {
      const n = Math.max(s.regardent || 0, s.participants || 0);
      const marque = s.ouvrir?.saison ? ` S${s.ouvrir.saison}E${s.ouvrir.episode}` : '';
      const b = el('button', 'ss-bouton primaire caisse-rejoindre', `Rejoindre${marque} · ${n} ${n > 1 ? 'personnes' : 'personne'}`);
      b.type = 'button';
      b.onclick = () => rejoindre(s.alias);
      zone.append(b);
    }
    if (!actif || estUnFilm) { zone.hidden = !zone.childElementCount; return; }
    try {
      const d = await api(`/api/points/serie/${encodeURIComponent(oeuvre.id)}`);
      if (d.action === 'deja') zone.append(el('span', 'caisse-debloquee', 'Série débloquée'));
      else if (d.action === 'acheter' && d.episodes > 0) {
        const b = el('button', 'ss-bouton caisse-serie');
        b.type = 'button';
        b.append(`Toute la série · `, el('s', null, `${d.reference}`), ` ${d.prix} pts`);
        b.title = textePrix(d);
        b.onclick = async () => { if (await acheterSerie(oeuvre.id, oeuvre.titre)) peindreActionsFiche(zone, oeuvre, seances); };
        zone.append(b);
      }
    } catch { /* la série complète est un plus : sans elle, la fiche reste entière */ }
    zone.hidden = !zone.childElementCount;
  }

  // --- « Mes points » : solde et journal ----------------------------------------
  const TYPES = { gain: 'Gain', achat: 'Achat', remise: 'Remise', remboursement: 'Remboursement' };
  async function peindreJournal(zone) {
    if (!zone) return;
    const m = await chargerMoi();
    zone.textContent = '';
    zone.append(el('p', 'caisse-solde', m?.solde === null || m?.solde === undefined ? 'Solde indisponible' : `${m.solde} points`));
    const lignes = m?.journal ?? [];
    if (!lignes.length) { zone.append(el('p', 'caisse-aide', 'Aucune transaction pour l’instant. Rejoindre un groupe est gratuit et rapporte des points.')); return; }
    const ul = el('ul', 'caisse-journal');
    for (const l of lignes.slice(0, 50)) {
      const li = el('li', `caisse-j caisse-j-${l.type}`);
      const quand = new Date(l.quand).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
      const signe = l.type === 'remise' ? `${l.montant} économisés` : `${l.montant > 0 ? '+' : ''}${l.montant}`;
      li.append(el('span', 'caisse-j-type', TYPES[l.type] ?? l.type), el('span', 'caisse-j-lib', `${l.libelle ?? ''}${l.annule ? ' (annulé)' : ''}`), el('span', 'caisse-j-mt', signe), el('span', 'caisse-j-quand', quand));
      // Annulable 10 min (le remboursement rend exactement ce qui a été débité).
      if (l.type === 'achat' && l.jeton && Date.now() - l.quand < 10 * 60_000) {
        const b = el('button', 'caisse-annuler', 'Annuler');
        b.type = 'button';
        b.onclick = async () => {
          b.disabled = true;
          try {
            const x = await api('/api/points/annuler', { method: 'POST', body: JSON.stringify({ jeton: l.jeton }) });
            direMot(`Achat annulé : ${x.rendu} points rendus`);
          } catch { direMot('Annulation impossible (plus de 10 min, ou déjà annulé).'); }
          peindreJournal(zone);
        };
        li.append(b);
      }
      ul.append(li);
    }
    zone.append(ul);
  }

  /** Le bonus de fin d'épisode (renvoyé par `/api/reprendre`). */
  function surBonus(b) {
    if (!(b?.bonus > 0)) return;
    majSolde(b.solde);
    direMot(`+${b.bonus} points · bonus de groupe`, 5000);
  }

  return {
    chargerMoi, majSolde, porteDuPrix, acheterSerie, peindreEnCeMoment, peindreActionsFiche, peindreJournal, surBonus,
    get actif() { return actif; }, get solde() { return solde; }, get confirmerDes() { return confirmerDes; },
    surSolde: (f) => { ecouteurs.add(f); return () => ecouteurs.delete(f); },
  };
}
